import { resolveContractorName, isContractorInternal } from "./PermitSignatureCard";
import { getPermitInternalTeamName } from "./PermitListMeta";

/**
 * Parses a YYYY-MM-DD date string into a Date object representing the start of the day (00:00:00.000)
 * in local timezone. Returns null if invalid.
 */
export const parseStartOfDay = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const parts = dateStr.trim().split("-");
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  const d = new Date(year, month, day, 0, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Parses a YYYY-MM-DD date string into a Date object representing the end of the day (23:59:59.999)
 * in local timezone (inclusive To Date boundary). Returns null if invalid.
 */
export const parseEndOfDay = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const parts = dateStr.trim().split("-");
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  const d = new Date(year, month, day, 23, 59, 59, 999);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Resolves canonical date for a permit:
 * - Submitted Permits: workflow_started_at -> submitted_at -> created_at.
 * - Draft Permits: created_at.
 * Resubmissions never overwrite original workflow_started_at.
 */
export const getPermitCanonicalDate = (permit) => {
  if (!permit) return null;
  const status = String(
    permit.workflow_summary?.current_status ?? permit.current_status ?? ""
  ).trim().toLowerCase();

  let rawDate = null;
  if (status === "draft") {
    rawDate = permit.created_at;
  } else {
    rawDate = permit.workflow_started_at || permit.submitted_at || permit.created_at;
  }

  if (!rawDate) return null;
  const d = new Date(rawDate);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Checks if a permit falls within the fromDate and toDate range (inclusive).
 * Safely handles inverted ranges (returns false) and invalid date strings.
 */
export const matchesPermitDateRange = (permit, fromDateStr, toDateStr) => {
  if (!fromDateStr && !toDateStr) return true;

  const startD = parseStartOfDay(fromDateStr);
  const endD = parseEndOfDay(toDateStr);

  // Inverted range check: fromDate > toDate
  if (startD && endD && startD.getTime() > endD.getTime()) {
    return false;
  }

  const permitDate = getPermitCanonicalDate(permit);
  if (!permitDate) return false;

  const pTime = permitDate.getTime();
  if (startD && pTime < startD.getTime()) {
    return false;
  }
  if (endD && pTime > endD.getTime()) {
    return false;
  }

  return true;
};

/**
 * Resolves the contractor identity & classification for a permit.
 * Bases classification on the permit's selected contractor, not creator or logged-in user.
 * Missing contractor names never automatically classify external permits as Internal.
 */
export const getPermitContractorClassification = (permit) => {
  if (!permit) {
    return {
      isInternal: false,
      contractorName: "",
      contractorId: "",
      isExternalUnassigned: false,
      displayText: "—",
    };
  }

  const rawContractorName = resolveContractorName(permit);
  const formData = permit.form_data || {};
  const contractorId = String(
    permit.contractor_id ||
    formData.contractor_id ||
    formData.contractorId ||
    (typeof permit.contractor === "object" ? permit.contractor?.id : "") ||
    ""
  ).trim();

  const normName = String(rawContractorName || "").trim();
  const internalFlag = isContractorInternal(normName, permit);

  // 1. Explicit external contractor name on permit
  if (normName && !internalFlag) {
    return {
      isInternal: false,
      contractorName: normName,
      contractorId,
      isExternalUnassigned: false,
      displayText: normName,
    };
  }

  // 2. Explicit internal contractor designation
  if (normName && internalFlag) {
    return {
      isInternal: true,
      contractorName: "",
      contractorId: "",
      isExternalUnassigned: false,
      displayText: getPermitInternalTeamName(permit),
    };
  }

  // 3. Missing contractor name: check if permit or creator indicates external contractor context
  const userType = String(permit.created_by_user_type || "").trim().toLowerCase();
  const creatorUserType = String(permit.created_by_profile?.user_type || "").trim().toLowerCase();
  const isExternalContext =
    userType === "external" ||
    creatorUserType === "external" ||
    permit.contractor_type === "EXTERNAL" ||
    permit.is_external === true;

  if (isExternalContext) {
    return {
      isInternal: false,
      contractorName: "",
      contractorId,
      isExternalUnassigned: true,
      displayText: "External (Unassigned)",
    };
  }

  // 4. Default fallback: Internal
  return {
    isInternal: true,
    contractorName: "",
    contractorId: "",
    isExternalUnassigned: false,
    displayText: getPermitInternalTeamName(permit),
  };
};

/**
 * Checks if a permit matches the selected contractor filter.
 * Supports:
 * - "all": any permit
 * - "internal": Internal permits only
 * - "external_unassigned": External permits with unassigned contractor
 * - ID-based matching (e.g. "id:401" or canonical contractor_id)
 * - Name-based matching (normalized, case-insensitive)
 */
export const matchesPermitContractor = (permit, filterValue) => {
  if (!filterValue || filterValue === "all") return true;

  const classification = getPermitContractorClassification(permit);

  if (filterValue === "internal") {
    return classification.isInternal;
  }

  if (filterValue === "external_unassigned") {
    return classification.isExternalUnassigned;
  }

  // If internal, it cannot match any external contractor filter
  if (classification.isInternal) {
    return false;
  }

  // Canonical ID matching preference
  if (filterValue.startsWith("id:")) {
    const targetId = filterValue.slice(3).trim();
    if (classification.contractorId && String(classification.contractorId) === targetId) {
      return true;
    }
  }

  // Direct contractorId match
  if (classification.contractorId && String(classification.contractorId) === filterValue) {
    return true;
  }

  // Name matching (normalized)
  const normFilter = filterValue.trim().toLowerCase();
  const normName = classification.contractorName.trim().toLowerCase();

  return normName === normFilter;
};

/**
 * Builds dynamic, deduplicated contractor dropdown options from permits and optional config list.
 * Disambiguates duplicate names with contractor IDs if available.
 */
export const buildContractorFilterOptions = (
  permits = [],
  extraContractors = [],
  explicitProjectId = null
) => {
  const internalLabel = getPermitInternalTeamName(permits[0] || null, explicitProjectId);
  const options = [
    { value: "all", label: "All Contractors" },
    { value: "internal", label: internalLabel },
  ];

  let hasExternalUnassigned = false;
  // Map of unique key -> { id, name, count }
  const contractorMap = new Map();
  // Name to array of IDs to detect duplicate names
  const nameToIds = new Map();

  // 1. Process permits
  permits.forEach((p) => {
    const info = getPermitContractorClassification(p);
    if (info.isInternal) return;

    if (info.isExternalUnassigned) {
      hasExternalUnassigned = true;
      return;
    }

    const name = info.contractorName;
    const cid = info.contractorId;
    const key = cid ? `id:${cid}` : `name:${name.toLowerCase()}`;

    if (!contractorMap.has(key)) {
      contractorMap.set(key, { id: cid, name });
    }

    const normName = name.toLowerCase();
    if (!nameToIds.has(normName)) {
      nameToIds.set(normName, new Set());
    }
    if (cid) {
      nameToIds.get(normName).add(cid);
    }
  });

  // 2. Process extra project contractors if passed
  extraContractors.forEach((c) => {
    if (!c) return;
    const name = typeof c === "string" ? c : c.name || c.contractor_name || "";
    const cid = typeof c === "object" ? String(c.id || c.contractor_id || "") : "";
    if (!name || ["internal", "internal flow"].includes(name.toLowerCase())) return;

    const key = cid ? `id:${cid}` : `name:${name.toLowerCase()}`;
    if (!contractorMap.has(key)) {
      contractorMap.set(key, { id: cid, name });
    }

    const normName = name.toLowerCase();
    if (!nameToIds.has(normName)) {
      nameToIds.set(normName, new Set());
    }
    if (cid) {
      nameToIds.get(normName).add(cid);
    }
  });

  // 3. Format contractor options, disambiguating duplicates
  const sortedContractors = Array.from(contractorMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  sortedContractors.forEach(({ id, name }) => {
    const idsForName = nameToIds.get(name.toLowerCase()) || new Set();
    const isDuplicateName = idsForName.size > 1;

    let value = name;
    let label = name;

    if (id) {
      value = `id:${id}`;
      if (isDuplicateName) {
        label = `${name} (ID: ${id})`;
      }
    }

    options.push({ value, label });
  });

  // 4. Add External (Unassigned) option if applicable
  if (hasExternalUnassigned) {
    options.push({
      value: "external_unassigned",
      label: "External (Unassigned)",
    });
  }

  return options;
};

/**
 * Evaluates all five filters against a permit:
 * - status
 * - type
 * - fromDate
 * - toDate
 * - contractor
 */
export const filterPermitItem = (permit, filters) => {
  if (!permit) return false;

  // 1. Status Filter
  if (filters.status && filters.status !== "all") {
    const currentStatus = String(
      permit.workflow_summary?.current_status ?? permit.current_status ?? ""
    ).trim().toLowerCase();
    if (currentStatus !== filters.status.trim().toLowerCase()) {
      return false;
    }
  }

  // 2. Type Filter (template)
  if (filters.type && filters.type !== "all") {
    const templateId = String(permit.template_id ?? permit.template?.id ?? "");
    if (templateId !== String(filters.type)) {
      return false;
    }
  }

  // 3 & 4. From Date and To Date
  if (filters.fromDate || filters.toDate) {
    if (!matchesPermitDateRange(permit, filters.fromDate, filters.toDate)) {
      return false;
    }
  }

  // 5. Contractor Filter
  if (filters.contractor && filters.contractor !== "all") {
    if (!matchesPermitContractor(permit, filters.contractor)) {
      return false;
    }
  }

  return true;
};
